// ============================================================
// ShareLinksModal.tsx — a document's secure share links (Documents list →
// the link icon on a row). Every link made for the file (emailed from the
// composer): who made it, when, when it expires, how often it was opened, and
// whether it's active, expired or cancelled. Its creator or an admin can
// CANCEL an active link — new downloads stop at once (the public download
// checks revokedAt); a download already started runs on its 5-minute signed
// R2 URL. Tokens are never shown (only their hash is stored).
// ============================================================
import React, { useEffect, useState } from 'react'
import ModalFrame from './ModalFrame'
import { listShareLinksApi, revokeShareLinkApi, type ShareLinkInfo } from '../../api/casevoApi'

const fmt = (iso: string): string => new Date(iso).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })
const STATUS: Record<ShareLinkInfo['status'], { label: string; cls: string }> = {
  active: { label: 'Active', cls: 'bg-casevo-mint text-casevo-brand dark:text-[#8fd6b0]' },
  expired: { label: 'Expired', cls: 'bg-casevo-input text-casevo-text-soft' },
  revoked: { label: 'Cancelled', cls: 'bg-casevo-danger-bg text-casevo-danger' },
}
const BTN = 'inline-flex items-center h-[30px] px-[11px] rounded-[8px] text-[12.5px] font-semibold border focus:outline-none casevo-focus disabled:opacity-50 whitespace-nowrap'

const ShareLinksModal: React.FC<{ fileId: string; filename: string; onClose: () => void }> = ({ fileId, filename, onClose }) => {
  const [links, setLinks] = useState<ShareLinkInfo[] | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [busyId, setBusyId] = useState<string | null>(null)

  const load = async (): Promise<void> => {
    try { setLinks(await listShareLinksApi(fileId)) } catch (e) { setError(e instanceof Error ? e.message : 'Could not load the share links.') }
  }
  useEffect(() => { void load() }, [fileId]) // eslint-disable-line react-hooks/exhaustive-deps

  const cancel = async (l: ShareLinkInfo): Promise<void> => {
    setBusyId(l.id)
    setError(null)
    try {
      await revokeShareLinkApi(fileId, l.id)
      await load()
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not cancel the link.')
    } finally {
      setBusyId(null)
    }
  }

  const active = (links ?? []).filter((l) => l.status === 'active').length
  return (
    <ModalFrame
      onClose={onClose}
      size="md"
      portal
      eyebrow="Secure share links"
      title={filename}
      subtitle={links === null ? 'Loading…' : links.length === 0 ? 'This document hasn’t been shared as a link.' : `${active} active link${active === 1 ? '' : 's'}. Cancelling one stops new downloads straight away — a download that has already started may still finish within 5 minutes.`}
      footerRight={<button type="button" className={`${BTN} border-casevo-border bg-casevo-surface text-casevo-forest`} onClick={onClose}>Close</button>}
    >
      {error && <p role="alert" className="mb-3 text-[13px] text-casevo-danger">{error}</p>}
      {links && links.length > 0 && (
        <ul aria-label="Share links" className="m-0 p-0 list-none rounded-[12px] border border-casevo-border-soft divide-y divide-casevo-border-soft">
          {links.map((l) => (
            <li key={l.id} className="px-[14px] py-[11px] flex flex-wrap items-center gap-x-4 gap-y-1">
              <div className="min-w-0 flex-1">
                <div className="text-[13.5px] text-casevo-forest">
                  Made by <b className="font-semibold">{l.createdByName ?? 'someone'}</b> on {fmt(l.createdAt)}
                </div>
                <div className="text-[12.5px] text-casevo-text-soft">
                  {l.status === 'revoked' && l.revokedAt ? `Cancelled ${fmt(l.revokedAt)}` : `${l.status === 'expired' ? 'Expired' : 'Expires'} ${fmt(l.expiresAt)}`}
                  {' · '}opened {l.accessCount} time{l.accessCount === 1 ? '' : 's'}{l.lastAccessedAt ? `, last ${fmt(l.lastAccessedAt)}` : ''}
                </div>
              </div>
              <span className={`text-[11px] font-bold uppercase tracking-[0.06em] px-2 py-[2px] rounded ${STATUS[l.status].cls}`}>{STATUS[l.status].label}</span>
              {l.canRevoke && (
                <button type="button" onClick={() => { void cancel(l) }} disabled={busyId === l.id}
                  className={`${BTN} border-casevo-danger-border text-casevo-danger bg-transparent hover:bg-casevo-danger-bg`}>
                  {busyId === l.id ? 'Cancelling…' : 'Cancel link'}
                </button>
              )}
            </li>
          ))}
        </ul>
      )}
    </ModalFrame>
  )
}

export default ShareLinksModal
